import { Chip, debounce, Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useState, useEffect } from 'react';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import useDistrictPickerHeader from 'src/shared/hooks/useDistrictPickerHeader';
import { useExtraMembershipLabel } from '../hooks/useExtraMembershipLabel';
import { MemberSearchFormState } from '../models/memberSearchFormState';

interface SelectedValue {
  field: string;
  value: any;
}

type MemberSelectionChipProps = {
  values: MemberSearchFormState | null | undefined;
  newValues: boolean;
};
export const MemberSelectionChip = (props: MemberSelectionChipProps) => {
  const { values, newValues } = props;
  const { pickerHeader } = useDistrictPickerHeader();

  const [selectedValuesArrays, setSelectedValuesArrays] = useState<SelectedValue[]>([]);
  const [selectedValuesSingleFields, setSelectedValuesSingleFields] = useState<SelectedValue[]>([]);

  const friendLabel = useExtraMembershipLabel();

  // Special handling for truthy/falsy fields and checkboxes
  const isTruthyFieldType = (key) => ['hasNewspaper', 'hasMobilePhone', 'hasEmailAddress'].includes(key);
  const isCheckBoxType = (key) => ['includeResignedTitles', 'isFamilyMember', 'isMainMember', 'isExtraMember', 'hasExtraMemberships'].includes(key);

  // Ignore fields that should not be a part of the form summary
  const isIgnoredFieldType = (key) =>
    ['status', 'pageSize', 'pageIndex', 'sortCol', 'sortDir', 'selectionId', 'selectionName', 'selectionInfo'].includes(key);

  const displayFormValue = (key, val, status?: boolean, units?: boolean, profiles?: boolean, titles?: boolean, county?: boolean) => {
    if (Array.isArray(val)) {
      return `(${val.length})`;
    }

    if (val !== undefined && ['resCause', 'sex'].includes(key)) {
      return val?.name;
    }

    if (val !== undefined && key.indexOf('Date') >= 0 && dayjs(val).isValid()) {
      return DateHelper.getDate(val);
    }

    if (isCheckBoxType(key)) {
      return t(`member:memberAdvancedSearch.${key}`, { label: friendLabel.toLowerCase() });
    }

    if (isTruthyFieldType(key)) {
      return t(`member:memberAdvancedSearch.${key}.${val === 'true' ? 'truthy' : 'false'}`);
    }
    if (status) {
      return t(`member:tabs.${key}`);
    }

    if (units || profiles || titles || county) {
      return t(key);
    }

    return val?.toString?.() ?? '';
  };

  const CustomChip = ({
    field,
    value,
    status = false,
    units = false,
    profiles = false,
    titles = false,
    county = false,
  }: {
    field: string;
    value: any;
    status?: boolean;
    units?: boolean;
    profiles?: boolean;
    titles?: boolean;
    county?: boolean;
  }) => {
    const titleSuffix = isTruthyFieldType(field);
    let chipTitle: string | JSX.Element = t(`member:memberAdvancedSearch.${field}${(titleSuffix && '.title') || ''}`);
    if (status || units || profiles || titles || county) {
      chipTitle = '';
    }

    if (isCheckBoxType(field)) chipTitle = '';

    return (
      <Chip
        color={newValues ? 'primary' : 'error'}
        key={field}
        label={
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, color: 'white', alignItems: 'center', mr: 1, pl: 0.5 }}>
            {chipTitle && (
              <Typography variant='body2' sx={{ fontWeight: 100, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {chipTitle}
              </Typography>
            )}
            <Typography variant='body2' sx={{ fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {displayFormValue(field, value, status, units, profiles, titles, county)}
            </Typography>
          </Box>
        }
      />
    );
  };

  useEffect(() => {
    if (values) {
      const debouncedSetSelectedValues = debounce(() => {
        // Filter out empty state and some field.
        const selected: SelectedValue[] = Object.entries(values)
          .filter(([key, value]) => {
            if (key === 'status' && Array.isArray(value) && value.length > 0) return true;
            if (isIgnoredFieldType(key)) return false;
            if (value !== undefined && value !== '' && isTruthyFieldType(key)) return true;
            if (key === 'includeResignedTitles' && typeof value === 'boolean' && value !== true) {
              return false;
            }
            if (Array.isArray(value) && value.length === 0) return false;
            if (value !== undefined && value !== '' && value !== null) return true;
            return false;
          })
          .map(([key, value]) => ({ field: key, value }));

        const arrays = selected.filter(({ value }) => Array.isArray(value));
        const singles = selected.filter(({ value }) => !Array.isArray(value));

        arrays.sort((a, b) => a.field.localeCompare(b.field));
        singles.sort((a, b) => a.field.localeCompare(b.field));

        setSelectedValuesArrays(arrays);
        setSelectedValuesSingleFields(singles);
      }, 300);

      debouncedSetSelectedValues();

      // Cleanup function to cancel debounce on component unmount or when values change
      return () => {
        debouncedSetSelectedValues.clear();
      };
    }
    return undefined;
  }, [values]);

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, mt: 0.5, mb: 1 }}>
      {selectedValuesArrays &&
        selectedValuesArrays.length > 0 &&
        selectedValuesArrays.map(({ field, value }) => {
          if (field === 'status' && Array.isArray(value)) {
            return (
              <Grid item xs={12} key={`${field}_container`}>
                <Typography variant='h6' sx={{ color: 'text.secondary' }}>
                  {t('member:memberAdvancedSearch.status')}
                </Typography>
                {value.map((status) => (
                  <Box mb={1} mr={1} key={`${field}_${status.key}`}>
                    <CustomChip field={status.key} value={status.key} status />
                  </Box>
                ))}
              </Grid>
            );
          }

          if (field === 'districtIds' && Array.isArray(value)) {
            return (
              <>
                <Grid item xs={12} mr={2} key={`${field}_container`}>
                  <Typography variant='h6' sx={{ color: 'text.secondary' }}>
                    {pickerHeader}
                  </Typography>
                  {value.map((unit) => (
                    <Box mb={1} mr={1} key={unit.code}>
                      <CustomChip field={unit.name} value={unit.key} units />
                    </Box>
                  ))}
                </Grid>
              </>
            );
          }

          if (field === 'excludedProfileItemIds' && Array.isArray(value)) {
            return (
              <>
                <Grid item xs={12} key={`${field}_container`}>
                  <Typography variant='h6' sx={{ color: 'text.secondary' }}>
                    {t('member:memberAdvancedSearch.excludedProfileItemIds')}
                  </Typography>
                  {value.map((profile) => (
                    <Box mb={1} mr={1} key={profile.code}>
                      <CustomChip field={profile.itemName} value={profile.key} key={profile.memberProfileItem_id} profiles />
                    </Box>
                  ))}
                </Grid>
              </>
            );
          }

          if (field === 'profileItemIds' && Array.isArray(value)) {
            return (
              <>
                <Grid item xs={12} key={`${field}_container`}>
                  <Typography variant='h6' sx={{ color: 'text.secondary' }}>
                    {t('member:memberAdvancedSearch.profileItemIds')}
                  </Typography>
                  {value.map((profile) => (
                    <Box mb={1} mr={1} key={profile.code}>
                      <CustomChip field={profile.itemName} value={profile.key} profiles />
                    </Box>
                  ))}
                </Grid>
              </>
            );
          }

          if (field === 'titleIds' && Array.isArray(value)) {
            return (
              <>
                <Grid item xs={12} key={`${field}_container`}>
                  <Typography variant='h6' sx={{ color: 'text.secondary' }}>
                    {t('member:tableHeaders.memberTitle')}
                  </Typography>
                  {value.map((title) => (
                    <Box mb={1} mr={1} key={title.parentCode}>
                      <CustomChip field={title.name} value={title.key} titles />
                    </Box>
                  ))}
                </Grid>
              </>
            );
          }

          if (field === 'countyCode' && Array.isArray(value)) {
            return (
              <>
                <Grid item xs={12} key={`${field}_container`}>
                  <Typography variant='h6' sx={{ color: 'text.secondary' }}>
                    {t('member:memberAdvancedSearch.countyCode')}
                  </Typography>
                  {value.map((county) => (
                    <Box mb={1} mr={1} key={county.code}>
                      <CustomChip field={county.name} value={county.key} county />
                    </Box>
                  ))}
                </Grid>
              </>
            );
          }

          return <CustomChip field={field} value={value} key={field} />;
        })}
      {selectedValuesSingleFields && selectedValuesSingleFields.length > 0 && (
        <Grid item xs={12}>
          <Typography variant='h6' mt={2} sx={{ color: 'text.secondary' }}>
            {t('member:selection.searchFields')}
          </Typography>
          {selectedValuesSingleFields.map(({ field, value }) => (
            <Box mb={1} mr={1} key={field}>
              <CustomChip field={field} value={value} />
            </Box>
          ))}
        </Grid>
      )}
    </Box>
  );
};
